<template>
  <div id="doAnswerView">
    <a-card>
      <h1>{{ app.appName }}</h1>
      <p>{{ app.appDesc }}</p>
      <h2 style="margin-bottom: 16px">
        {{ current }}、{{ currentQuestion?.title }}
      </h2>
      <div>
        <a-radio-group
          direction="vertical"
          v-model="currentAnswer"
          :options="questionOptions"
          @change="doRadioChange"
        />
      </div>
      <div style="margin-top: 24px">
        <a-space size="large">
          <a-button
            type="primary"
            circle
            v-if="current < questionContent.length"
            :disabled="!currentAnswer"
            @click="current += 1"
          >
            下一题
          </a-button>
          <a-button
            type="primary"
            v-if="current === questionContent.length"
            :loading="submitting"
            circle
            :disabled="!currentAnswer"
            @click="doSubmit"
          >
            {{ submitting ? "评分中" : "查看结果" }}
          </a-button>
          <a-button v-if="current > 1" circle @click="current -= 1">
            上一题
          </a-button>
        </a-space>
      </div>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watchEffect,
  //@ts-ignore
  withDefaults,
  //@ts-ignore
  defineProps,
  reactive,
  computed,
} from "vue";
import API from "@/api";
import { getAppVoByIdUsingGet } from "@/api/appController";
import message from "@arco-design/web-vue/es/message";
import { listQuestionVoByPageUsingPost } from "@/api/questionController";
import {
  addUserAnswerUsingPost,
  generateUserAnswerIdUsingGet,
} from "@/api/userAnswerController";
import { useRouter } from "vue-router";

// 拉取题目结构
const questionContent = ref<API.QuestionContentDTO[]>([]);
const app = ref<API.AppVO>({});

// 序号
const current = ref(1);
// 当前题目
const currentQuestion = ref<API.QuestionContentDTO>({});
// 当前题目选项
const questionOptions = computed(() => {
  return currentQuestion.value?.options
    ? currentQuestion.value.options.map((option) => {
        return {
          label: `${option.key}、${option.value}`,
          value: option.key,
        };
      })
    : [];
});

// 选中后保存记录
const doRadioChange = (value: string) => {
  answerList[current.value - 1] = value;
};

// 当前答案
const currentAnswer = ref<string>();
// 回答列表
const answerList = reactive<string[]>([]);
//提交状态
const submitting = ref(false);

interface Props {
  appId: string;
}

const props = withDefaults(defineProps<Props>(), {
  appId: () => {
    return "";
  },
});

// 加载数据
const loadData = async () => {
  if (!props.appId) {
    return;
  }
  // 获取应用信息 -- ref 获取API的AppVO对象,并赋值给app.value,经过校验,被赋值 res.data.data
  let res: any = await getAppVoByIdUsingGet({
    id: props.appId as any,
  });

  if (res.data.code === 0) {
    app.value = res.data.data as any;
  } else {
    message.error("获取数据失败," + res.data.message);
  }

  // 获取题目内容
  res = await listQuestionVoByPageUsingPost({
    appId: props.appId as any,
    current: 1,
    pageSize: 1,
    sortField: "createTime",
    sortOrder: "descend",
  });
  if (res.data.code === 0 && res.data.data?.records) {
    questionContent.value = res.data.data.records[0].questionContent;
  } else {
    message.error("获取题目失败," + res.data.message);
  }
};

// 唯一 id
const id = ref<number>();

// 生成唯一 id
const generateId = async () => {
  const res = await generateUserAnswerIdUsingGet();
  if (res.data.code === 0) {
    id.value = res.data.data as any;
  } else {
    message.error("获取唯一 id 失败，" + res.data.message);
  }
};

// 进入页面时，生成唯一 id
watchEffect(() => {
  generateId();
});

/**
 * 监听 searchParams 变量：触发加载
 */
watchEffect(() => {
  loadData();
});

// 监听 current 变量：更新 currentQuestion
watchEffect(() => {
  currentQuestion.value = questionContent.value[current.value - 1];
  currentAnswer.value = answerList[current.value - 1];
});

const router = useRouter();

const doSubmit = async () => {
  if (!props.appId || !answerList.length) {
    return;
  }
  submitting.value = true;
  const res = await addUserAnswerUsingPost({
    appId: props.appId as any,
    choices: answerList,
    id: id.value as any,
  });
  if (res.data.code === 0 && res.data.data) {
    message.success("提交成功");
    router.push(`/answer/result/${res.data.data}`);
  } else {
    message.error("提交失败," + res.data.message);
  }
  submitting.value = false;
};
</script>

<style scoped>
#doAnswerView {
}

#doAnswerView .content-wrapper > * {
  margin-bottom: 24px;
}
</style>
